import { create } from "zustand";
import type { User } from "@supabase/supabase-js";
import type { Profile } from "./useProfile";
import { supabaseClient } from "../utils/supabase";

interface AuthState {
    user: User | null;
    profile: Profile | null;
    isAuthenticated: boolean;
    hasProfile: boolean;
    id: string;
    phone: string;
    isAdmin: boolean;
    isLoading: boolean;
    isStalling: boolean;
    setAuthData: (user: User | null, profile: Profile | null, isLoading?: boolean) => void;
    setPhone: (phone: string) => void;
    signOut: () => Promise<void>;
    initialize: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
    user: null,
    profile: null,
    isAuthenticated: false,
    hasProfile: false,
    id: "",
    phone: "",
    isAdmin: false,
    isLoading: true,
    isStalling: true,

    setAuthData: (user: User | null, profile: Profile | null, isLoading = false) => {
        set({
            user,
            profile,
            isAuthenticated: !!user,
            hasProfile: !!profile,
            id: user?.id || "",
            phone: profile?.phone || user?.phone || "",
            isAdmin: profile?.is_admin || false,
            isLoading,
            isStalling: false,
        });
    },

    setPhone: (phone: string) => {
        set({ phone });
    },

    signOut: async () => {
        try {
            // Sign out from Supabase
            await supabaseClient.auth.signOut();

            // Clear local state
            set({
                user: null,
                profile: null,
                isAuthenticated: false,
                hasProfile: false,
                id: "",
                phone: "",
                isAdmin: false,
                isLoading: false,
                isStalling: false,
            });
        } catch (error) {
            console.error("Sign out error:", error);
            // Even if Supabase sign out fails, clear local state
            set({
                user: null,
                profile: null,
                isAuthenticated: false,
                hasProfile: false,
                id: "",
                phone: "",
                isAdmin: false,
                isLoading: false,
                isStalling: false,
            });
        }
    },

    initialize: async () => {
        try {
            const {
                data: { session },
            } = await supabaseClient.auth.getSession();
            if (session?.user) {
                // Restore auth state from session
                const { data: profile } = await supabaseClient
                    .from("profiles")
                    .select("profile_id, phone, is_admin")
                    .eq("profile_id", session.user.id)
                    .maybeSingle();

                get().setAuthData(session.user, profile);
            } else {
                get().setAuthData(null, null, false);
            }

            // Set up auth state change listener
            supabaseClient.auth.onAuthStateChange((event, session) => {
                if (event === "SIGNED_OUT") {
                    get().setAuthData(null, null, false);
                } else if (event === "SIGNED_IN" && session?.user) {
                    // Handle sign in - fetch profile
                    supabaseClient
                        .from("profiles")
                        .select("profile_id, phone, is_admin")
                        .eq("profile_id", session.user.id)
                        .maybeSingle()
                        .then(({ data: profile }) => {
                            get().setAuthData(session.user, profile);
                        });
                }
            });
        } catch (error) {
            console.error("Auth initialization error:", error);
            get().setAuthData(null, null, false);
        }
    },
}));
